/**
 * ReviewDrawOverlay
 *
 * A lightweight SVG freehand-draw overlay for review annotations.
 * Strokes are stored per version-number in reviewStore (in-memory, not persisted).
 * The SVG transform tracks pan/zoom imperatively via spine-manager viewport events.
 */
import { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { getStroke } from 'perfect-freehand';
import { nanoid } from 'nanoid';
import { getSpineManager, type ViewportState } from '../../lib/spine-manager';
import { useReviewStore } from '../../stores/reviewStore';
import type { DrawStroke, DrawStrokePoint } from '@spine-workspace/shared';

const RULER_SIZE = 20;

// ─── SVG stroke rendering ─────────────────────────────────────────────────────

function pointsToSvgPath(points: DrawStrokePoint[], size: number): string | null {
  if (points.length < 2) return null;
  const raw = points.map((p) => [p.x, p.y] as [number, number]);
  const outline = getStroke(raw, {
    size: size * 2.5,
    thinning: 0.5,
    smoothing: 0.5,
    streamline: 0.4,
    last: true,
  });
  if (outline.length < 2) return null;

  // Build SVG path from outline polygon
  const d = outline.reduce((acc, [x, y], i) => {
    return acc + (i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`);
  }, '');
  return `${d} Z`;
}

interface StrokeSvgProps {
  stroke: DrawStroke;
}

function StrokeSvg({ stroke }: StrokeSvgProps) {
  const d = pointsToSvgPath(stroke.points, stroke.size);
  if (!d) return null;
  return <path d={d} fill={stroke.color} opacity={0.85} />;
}

// ─── Live preview stroke (while drawing) ──────────────────────────────────────

interface LiveStrokeProps {
  points: DrawStrokePoint[];
  color: string;
  size: number;
}

function LiveStroke({ points, color, size }: LiveStrokeProps) {
  const d = pointsToSvgPath(points, size);
  if (!d) return null;
  return <path d={d} fill={color} opacity={0.85} />;
}

// ─── Main overlay ──────────────────────────────────────────────────────────────

export default function ReviewDrawOverlay() {
  const { drawingVersion, reviewStrokes, reviewDrawColor, addReviewStroke, activeVersion } =
    useReviewStore();
  const svgRef = useRef<SVGSVGElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const vpRef = useRef<ViewportState>(getSpineManager().getViewportState());

  const [livePoints, setLivePoints] = useState<DrawStrokePoint[]>([]);
  const isDrawing = useRef(false);

  // Track viewport imperatively — no React state updates for pan/zoom
  useLayoutEffect(() => {
    function applyVp(vp: ViewportState) {
      vpRef.current = vp;
      if (groupRef.current) {
        groupRef.current.setAttribute(
          'transform',
          `translate(${RULER_SIZE + vp.x}, ${RULER_SIZE + vp.y}) scale(${vp.zoom})`
        );
      }
    }
    applyVp(getSpineManager().getViewportState());
    return getSpineManager().onViewportChange(applyVp);
  }, []);

  // Which strokes to show: the drawingVersion if active, else the active version filter
  const visibleVersion = drawingVersion ?? (activeVersion === 'all' ? null : activeVersion);
  const visibleStrokes: DrawStroke[] = visibleVersion != null
    ? (reviewStrokes[visibleVersion] ?? [])
    : [];

  const screenToWorld = useCallback((sx: number, sy: number): DrawStrokePoint => {
    const vp = vpRef.current;
    return {
      x: (sx - RULER_SIZE - vp.x) / vp.zoom,
      y: (sy - RULER_SIZE - vp.y) / vp.zoom,
    };
  }, []);

  // ── Pointer handlers ───────────────────────────────────────────────────────

  function handlePointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (drawingVersion == null) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    isDrawing.current = true;
    const rect = e.currentTarget.getBoundingClientRect();
    const pt = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    setLivePoints([pt]);
  }

  function handlePointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (!isDrawing.current || drawingVersion == null) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pt = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
    setLivePoints((prev) => [...prev, pt]);
  }

  function handlePointerUp() {
    if (!isDrawing.current || drawingVersion == null) return;
    isDrawing.current = false;
    if (livePoints.length >= 2) {
      const stroke: DrawStroke = {
        id: nanoid(12),
        type: 'pen',
        color: reviewDrawColor,
        size: 4,
        isEraser: false,
        points: livePoints,
      };
      addReviewStroke(drawingVersion, stroke);
    }
    setLivePoints([]);
  }

  // Don't render anything if there's nothing to show and not in draw mode
  if (drawingVersion == null && visibleStrokes.length === 0) return null;

  const isDraw = drawingVersion != null;

  return (
    <svg
      ref={svgRef}
      className={`absolute inset-0 z-25 w-full h-full overflow-hidden ${
        isDraw ? 'cursor-crosshair pointer-events-auto' : 'pointer-events-none'
      }`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <g ref={groupRef}>
        {/* Committed strokes */}
        {visibleStrokes.map((s) => (
          <StrokeSvg key={s.id} stroke={s} />
        ))}
        {/* Live preview while drawing */}
        {livePoints.length >= 2 && (
          <LiveStroke points={livePoints} color={reviewDrawColor} size={4} />
        )}
      </g>
    </svg>
  );
}
